import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import { useClientSearch, type ClientDTO } from '../../hooks/useClients';

interface ClientAutocompleteProps {
  value: ClientDTO | null;
  onChange: (client: ClientDTO | null) => void;
  onCreateNew?: (nombre: string) => void;
  error?: string;
  disabled?: boolean;
}

export function ClientAutocomplete({
  value,
  onChange,
  onCreateNew,
  error,
  disabled,
}: ClientAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value?.nombre ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: searchData, isLoading } = useClientSearch(debouncedSearch);
  const clients = searchData?.data ?? [];

  // Debounce search input (300ms per AC)
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
    if (value) {
      onChange(null);
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  }, [value, onChange]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Sync input when value changes externally
  useEffect(() => {
    if (value) {
      setInputValue(value.nombre);
    }
  }, [value]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSelect = (client: ClientDTO) => {
    onChange(client);
    setInputValue(client.nombre);
    setIsOpen(false);
  };

  const showDropdown = isOpen && inputValue.length >= 2;
  const hasNoResults = !isLoading && clients.length === 0 && debouncedSearch.length >= 2;

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-sm font-medium text-gray-700">
        Nombre del cliente
      </label>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          className={`w-full rounded-lg border bg-white/80 py-2 pl-9 pr-3 text-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 ${
            error
              ? 'border-red-300 focus:border-red-400'
              : 'border-gray-200 focus:border-brand-blue'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          placeholder="Ej: Diego Cueva"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
        />
        {isLoading && (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
          />
        )}
      </div>

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/30 bg-white/95 shadow-lg backdrop-blur-md">
          <div className="max-h-48 overflow-y-auto">
            {clients.map((client) => (
              <button
                key={client.id}
                type="button"
                className="flex w-full flex-col px-3 py-2 text-left transition-colors hover:bg-gray-50"
                onClick={() => handleSelect(client)}
              >
                <span className="text-sm font-medium text-gray-800">
                  {client.nombre}
                </span>
                {client.ruc_ci && (
                  <span className="text-xs text-gray-400">
                    RUC/CI: {client.ruc_ci}
                  </span>
                )}
              </button>
            ))}

            {hasNoResults && (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                No se encontraron clientes
              </div>
            )}
          </div>

          {/* Create new option */}
          {onCreateNew && inputValue.length >= 3 && (
            <>
              <hr className="border-gray-100" />
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-blue transition-colors hover:bg-blue-50"
                onClick={() => {
                  onCreateNew(inputValue);
                  setIsOpen(false);
                }}
              >
                <Plus size={14} />
                Crear cliente &quot;{inputValue}&quot;
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
